import { useEffect, useState, useRef } from 'react';

// AWS Lambda API Gateway URL
const LAMBDA_API_URL = 'https://not47wmc3f.execute-api.us-east-1.amazonaws.com/';

// Session keys
const SESSION_KEY = 'awsVisitCountedThisSession';
const COUNT_KEY = 'awsCurrentVisitCount';
const LOCK_KEY = 'awsCountingInProgress';

// Custom hook to track and return GLOBAL visit count from AWS Lambda + DynamoDB
export const useVisitCounter = () => {
  const [visitCount, setVisitCount] = useState(150); // Fallback value while loading
  const hasCalledRef = useRef(false); // Prevent multiple calls from same instance

  useEffect(() => {
    // SYNCHRONOUS CHECKS (prevent race conditions)
    
    // 1. Check if already counted in this session
    const alreadyCounted = sessionStorage.getItem(SESSION_KEY);
    if (alreadyCounted) {
      const storedCount = sessionStorage.getItem(COUNT_KEY);
      if (storedCount) {
        setVisitCount(parseInt(storedCount, 10));
      }
      return;
    }

    // 2. Check if this component instance already called
    if (hasCalledRef.current) {
      return;
    }

    // 3. Check if another instance is currently counting (use sessionStorage as lock)
    const lockActive = sessionStorage.getItem(LOCK_KEY);
    if (lockActive) {
      // Another call in progress, wait for it to finish
      const checkInterval = setInterval(() => {
        const counted = sessionStorage.getItem(SESSION_KEY);
        if (counted) {
          const storedCount = sessionStorage.getItem(COUNT_KEY);
          if (storedCount) {
            setVisitCount(parseInt(storedCount, 10));
          }
          clearInterval(checkInterval);
        }
      }, 100);
      
      // Clear interval after 3 seconds
      setTimeout(() => clearInterval(checkInterval), 3000);
      return;
    }

    // Set locks
    hasCalledRef.current = true;
    sessionStorage.setItem(LOCK_KEY, 'true');

    const incrementVisitCount = async () => {
      try {
        console.log('📊 Calling visit counter Lambda (POST)...');
        
        // First visit in session - call Lambda to increment
        const response = await fetch(LAMBDA_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          mode: 'cors',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ AWS Lambda response:', data);

        if (data.success && data.visitCount) {
          const count = data.visitCount;
          setVisitCount(count);
          
          // Store in session to prevent double counting
          sessionStorage.setItem(SESSION_KEY, 'true');
          sessionStorage.setItem(COUNT_KEY, count.toString());
        } else {
          console.warn('Unexpected response format:', data);
          setVisitCount(150); // Fallback
        }

      } catch (error) {
        console.error('❌ Error fetching visit count from AWS Lambda:', error);
        
        // Fallback to localStorage if Lambda fails
        const localCount = localStorage.getItem('portfolioVisitCountFallback') || '150';
        const newCount = parseInt(localCount, 10) + 1;
        localStorage.setItem('portfolioVisitCountFallback', newCount.toString());
        setVisitCount(newCount);
      } finally {
        // Always release the lock
        sessionStorage.removeItem(LOCK_KEY);
      }
    };

    incrementVisitCount();
  }, []);

  return visitCount;
};

const VisitCounter = () => {
  const visitCount = useVisitCounter();
  return visitCount;
};

export default VisitCounter;